import type {
    FC,
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import { useState } from 'react'
import { ChevronDown, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'

export const controlClass = (invalid?: boolean, className?: string): string => cn(
    'w-full rounded-xl border bg-white px-3.5 text-[15px] text-foreground shadow-xs transition-colors outline-none placeholder:text-muted-foreground/70 focus-visible:border-gold focus-visible:ring-3 focus-visible:ring-gold/25 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-80',
    invalid ? 'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20' : 'border-input',
    className
)

interface FieldProps {
    label: ReactNode
    htmlFor: string
    error?: string | null
    hint?: ReactNode
    optional?: string
    className?: string
    children: ReactNode
}

export const Field: FC<FieldProps> = ({ label, htmlFor, error, hint, optional, className, children }): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <label htmlFor={htmlFor} className='text-sm font-semibold text-foreground'>
                {label}
                {optional && <span className='ms-1.5 font-normal text-muted-foreground'>({optional})</span>}
            </label>

            {children}

            {hint && !error && <p className='text-xs text-muted-foreground'>{hint}</p>}

            {error && (
                <p id={`${htmlFor}-error`} role='alert' className='text-xs font-medium text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    invalid?: boolean
}

export const Input: FC<InputProps> = ({ invalid, className, id, ...rest }): ReactNode => (
    <input
        id={id}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid && id ? `${id}-error` : undefined}
        className={controlClass(invalid, cn('h-11', className))}
        {...rest}
    />
)

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    invalid?: boolean
}

export const Textarea: FC<TextareaProps> = ({ invalid, className, id, rows = 4, ...rest }): ReactNode => (
    <textarea
        id={id}
        rows={rows}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid && id ? `${id}-error` : undefined}
        className={controlClass(invalid, cn('min-h-24 py-2.5 leading-relaxed', className))}
        {...rest}
    />
)

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    invalid?: boolean
}

export const Select: FC<SelectProps> = ({ invalid, className, id, children, ...rest }): ReactNode => (
    <div className='relative'>
        <select
            id={id}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid && id ? `${id}-error` : undefined}
            className={controlClass(invalid, cn('h-11 cursor-pointer appearance-none pe-10', className))}
            {...rest}
        >
            {children}
        </select>

        <ChevronDown className='pointer-events-none absolute end-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
    </div>
)

interface PasswordInputProps extends Omit<InputProps, 'type'> {
    showLabel: string
    hideLabel: string
}

export const PasswordInput: FC<PasswordInputProps> = ({ showLabel, hideLabel, className, ...rest }): ReactNode => {
    const [visible, setVisible] = useState(false)

    return (
        <div className='relative'>
            <Input type={visible ? 'text' : 'password'} className={cn('pe-11', className)} {...rest} />

            <button
                type='button'
                onClick={() => setVisible(value => !value)}
                aria-label={visible ? hideLabel : showLabel}
                aria-pressed={visible}
                className='absolute inset-y-0 end-0 flex w-11 items-center justify-center rounded-e-xl text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none'
            >
                {visible ? <EyeOff className='size-4' aria-hidden='true' /> : <Eye className='size-4' aria-hidden='true' />}
            </button>
        </div>
    )
}

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label: ReactNode
    invalid?: boolean
}

export const Checkbox: FC<CheckboxProps> = ({ label, invalid, id, className, ...rest }): ReactNode => (
    <label htmlFor={id} className={cn('flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-foreground/90', className)}>
        <input
            id={id}
            type='checkbox'
            aria-invalid={invalid || undefined}
            className={cn('mt-0.5 size-4.5 shrink-0 cursor-pointer rounded border-input', invalid && 'outline-2 outline-destructive')}
            {...rest}
        />
        <span>{label}</span>
    </label>
)
